import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { FormikConfig, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { DialogActions, DialogBody, DialogTitle, makeStyles, mergeClasses } from '@fluentui/react-components'
import {
    bundleIcon,
    DeleteFilled,
    DeleteRegular,
    DismissFilled,
    DismissRegular,
    SaveEditFilled,
    SaveEditRegular,
    SaveFilled,
    SaveRegular,
} from '@fluentui/react-icons'
import { AxiosError } from 'axios'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import { TaskMutationDto, TaskOverviewDto } from '../../../../config/taskConfig'
import { mapTaskOverviewDtoToMutation } from '../../../config/taskMutationConfig'
import TaskMutationDialogBodyContent from './TaskMutationDialogBodyContent'
import TooltippedButton from '../../Button/TooltippedButton'
import Loading from '../../../../components/Loading'
import useAxios from '../../../../hooks/useAxios'
import useEntity from '../../../../hooks/useEntity'
import useAppToast from '../../../../hooks/useAppToast'
import { useApplicationStyles } from '../../../../pages/App'
import { useCounter } from 'usehooks-ts'
import { formatIsoDate } from '../../../../helpers/dateHelper'
import { ResolvedTaskDateConfig, resolveTaskDateConfig } from '../../../../helpers/taskDateHelper'

const CloseIcon = bundleIcon(DismissFilled, DismissRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)
const SaveAndNewIcon = bundleIcon(SaveEditFilled, SaveEditRegular)
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular)

export type TaskDialogBodyProps = BaseMutableOverviewDialogProps<TaskOverviewDto> & {
    /** Base url of the overview, used to build the deep link in the "e-mail about task" body. */
    baseUrl?: string
}

export type TaskMutationDialogProps = HammockFormDialogProps<TaskMutationDto, TaskDialogBodyProps>

const useStyles = makeStyles({
    actions: { width: '100%' },
})

/**
 * Add/edit dialog for a task (HAM-4275). Hosted by the task overview through BaseMutableOverview, and by PopupContext
 * on the screens without a task overview. Modelled on TimeMutationDialogBody.
 */
export default function TaskMutationDialogBody(props: TaskMutationDialogProps): ReactElement {
    const axios = useAxios()
    const toast = useAppToast()
    const styles = useStyles()
    const appStyles = useApplicationStyles()
    const { t: translate } = useTranslation()
    const { values, initialValues, submitForm, isSubmitting, resetForm } = useFormikContext<TaskMutationDto>()

    const [submitVariant, setSubmitVariant] = useState<'save' | 'saveAndNew'>('save')
    // Bumped on open and after "save and new", so the cursor lands on the first field (AC20, AC28). Same mechanism as
    // the time dialog; an autoFocus on one specific field would fight with it whenever that field is not the first one.
    const { count: focusTrigger, increment: updateFocusTrigger } = useCounter(0)

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as TaskDialogBodyProps), [props])
    const id = dialogBodyProps.id

    // Opened from a deep link (#/task/<id>/edit): there is no overview item to start from, so the task is fetched here.
    const needsFetch = !!id && !dialogBodyProps.item
    // null keeps useEntity from firing; false would mean "this endpoint needs no id" and would still request /task/false.
    const [entity] = useEntity<TaskMutationDto>(entityId => ({ url: `/task/${entityId}` }), needsFetch ? id : null)

    useEffect(() => {
        if (needsFetch && entity.loaded && entity.data) {
            resetForm({ values: { ...entity.data, attachmentIds: entity.data.attachmentIds || [] } })
        }
    }, [entity.loaded])

    // The task was removed while the link was being followed.
    useEffect(() => {
        if (needsFetch && entity.rejected) {
            toast.showMessage(translate('task.edit.gone'), { intent: 'error' })
            props.onDismiss?.()
        }
    }, [entity.rejected])

    const handleSubmit = useCallback<FormikConfig<TaskMutationDto>['onSubmit']>(
        task => {
            if (id) {
                return axios.put(`/task/${id}`, task).catch((error: AxiosError) => {
                    if (error?.response?.status === 404) {
                        toast.showMessage(translate('task.edit.gone'), { intent: 'error' })
                        return undefined
                    }
                    throw error
                })
            }

            return axios
                .post('/task', task)
                .then(() => {
                    if (submitVariant === 'saveAndNew') {
                        // Keep what makes the next task in a row quick to enter; the deadline returns to its default.
                        resetForm({
                            values: {
                                ...initialValues,
                                fileId: task.fileId,
                                assigneeId: task.assigneeId,
                                assignerId: task.assignerId,
                            },
                        })
                        updateFocusTrigger()
                    } else {
                        resetForm()
                    }
                })
                .then(() => submitVariant === 'saveAndNew')
        },
        [id, submitVariant, initialValues],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    useEffect(() => updateFocusTrigger(), [])

    const handleDelete = () =>
        axios
            .delete(`/task/${id}`)
            .catch((error: AxiosError) => {
                if (error?.response?.status !== 404) {
                    throw error
                }
                toast.showMessage(translate('task.edit.gone'), { intent: 'error' })
            })
            .then(() => {
                resetForm()
                props.onDismiss?.()
            })

    // Cancel is the explicit way out: it discards without asking. Escape and a click next to the dialog do not close a
    // form with unsaved changes at all; the body shows a message pointing here instead (HAM-4275).
    const handleCancel = () => {
        resetForm()
        props.onDismiss?.()
    }

    const deleteLocked = !!values.workflowStepName

    if (needsFetch && !entity.loaded) {
        return (
            <DialogBody>
                <DialogTitle>{translate('task.edit.dialog.edit')}</DialogTitle>
                <Loading />
                <DialogActions />
            </DialogBody>
        )
    }

    return (
        <DialogBody>
            <DialogTitle>{translate(id ? 'task.edit.dialog.edit' : 'task.edit.dialog.create')}</DialogTitle>
            <TaskMutationDialogBodyContent {...props} focusTrigger={focusTrigger} />
            <DialogActions>
                <div className={mergeClasses(styles.actions, appStyles.spaceBetween)}>
                    {id && (
                        <TooltippedButton
                            appearance="subtle"
                            display="iconAndLabel"
                            label={translate('action.delete')}
                            tooltip={deleteLocked ? translate('task.edit.delete.locked') : undefined}
                            icon={<DeleteIcon />}
                            onClick={() => handleDelete()}
                            disabled={isSubmitting || deleteLocked}
                        />
                    )}
                    {!id && (
                        <TooltippedButton
                            appearance="subtle"
                            label={translate('action.saveandnew')}
                            icon={<SaveAndNewIcon />}
                            disabled={isSubmitting}
                            onClick={() => {
                                setSubmitVariant('saveAndNew')
                                submitForm()
                            }}
                        />
                    )}
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <TooltippedButton
                            appearance="secondary"
                            label={translate('action.cancel')}
                            icon={<CloseIcon />}
                            disabled={isSubmitting}
                            onClick={handleCancel}
                        />
                        <TooltippedButton
                            appearance="primary"
                            label={translate(isSubmitting ? 'base.saving' : 'action.save')}
                            icon={<SaveIcon />}
                            disabled={isSubmitting}
                            onClick={() => {
                                setSubmitVariant('save')
                                submitForm()
                            }}
                        />
                    </div>
                </div>
            </DialogActions>
        </DialogBody>
    )
}

/**
 * Initial values for a task that is being added. Shared by every entry point so the defaults cannot drift apart.
 * The office decides per date whether it starts on today or empty, and it decides the duration a new task starts on
 * (HAM-4286); without a configuration this is the behaviour of before that ticket.
 */
export function getNewTaskValues(defaults: Partial<TaskMutationDto>, dateConfig?: ResolvedTaskDateConfig): TaskMutationDto {
    const dates = dateConfig ?? resolveTaskDateConfig(undefined)
    const today = formatIsoDate(new Date())

    return {
        status: undefined,
        title: '',
        description: undefined,
        // A date that does not take part stays empty; the task then only carries the date the user filled in.
        start: dates.start.visible && dates.start.prefillToday ? today : undefined,
        due: dates.due.visible && dates.due.prefillToday ? today : undefined,
        duration: dates.duration.initialValue,
        assigneeId: undefined,
        assignerId: undefined,
        fileId: undefined,
        attachmentIds: [],
        highlight: false,
        today: false,
        week: false,
        ...defaults,
    }
}

/** Initial values for editing, straight from an overview item. */
export function getEditTaskValues(item: TaskOverviewDto): TaskMutationDto {
    return mapTaskOverviewDtoToMutation(item)
}
